<template>
    <modal ref="modal" class="authorization-modal" @closed="onCloseModal" @opened="onOpenModal">
        <template #button="sp">
            <slot name="button" :show-modal="sp.showModal"></slot>
        </template>
        <template #body="sp">
            <div class="alert border-danger text-danger">
                <p class="font-weight-bold">
                Attention : vous tentez de faire une opération dangereuse qui nécessite des privilèges élevés.<br>
                Merci de contacter un utilisateur autorisé pour pouvoir effectuer cette action.
                    <template v-if="authorizedPeople">
                        <br>
                        {{ authorizedPeople }}
                    </template>
                </p>
                <slot name="messages"></slot>
            </div>
            <div class="text-center">
                <button class="btn btn-outline-success mt-4" :disabled="posting" @click="sp.closeModal()">
                    <i class="fad fa-backward"></i> Annuler et retourner au formulaire
                </button>
            </div>
            <div class="h3 mt-4">
                Choisissez une méthode de validation :
            </div>

            <errors-zone :errors="errorsPosting" />
            <errors-zone :errors="errorsGooglePopup" />

            <div class="row authorization-forms">
                <div class="col border border-dark rounded ml-4">
                    <div class="h4 mt-4 font-weight-bold text-center">
                        Scan de badge
                    </div>
                    <div class="mt-2 text-center">
                        <p>Scannez le badge d'un utilisateur autorisé avec l'appareil photo</p>
                        <template v-if="cameraDisplayed">
                            <qrcode-stream class="my-4" :camera="cameraSelected" @decode="onCameraDecode" @init="onCameraInit">
                                <div v-if="cameraLoading">
                                    <i class="fad fa-spinner fa-spin"></i> Chargement...
                                </div>

                                <div v-if="cameraValidationPending">
                                    <i class="fad fa-spinner fa-spin"></i> Validation en cours...
                                </div>

                                <button @click="cameraSwitch">
                                    <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" width="24" height="24" viewBox="0 0 24 24"><path d="M15,15.5V13H9V15.5L5.5,12L9,8.5V11H15V8.5L18.5,12M20,4H16.83L15,2H9L7.17,4H4A2,2 0 0,0 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z"/>
                                    </svg>
                                </button>
                            </qrcode-stream>
                        </template>
                        <template v-else-if="!legacyMode">
                            <i class="fad fa-camera text-danger fa-8x d-block cursor-pointer" @click="onClickScan"></i>
                            <button class="btn btn-danger my-4" @click="onClickScan">
                                Cliquez ici pour scanner
                            </button>
                        </template>
                        <div v-if="cameraError" class="alert alert-danger">
                            {{ cameraError }}
                        </div>
                        <div v-if="legacyMode" class="my-4">
                            Si votre navigateur ne permet pas d'utiliser la méthode classique, scannez le code avec la méthode alternative ci-dessous :
                            <div class="mt-4">
                                <qrcode-dropzone @decode="onCameraDecode">
                                    <qrcode-capture @decode="onCameraDecode"></qrcode-capture>
                                </qrcode-dropzone>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col d-flex flex-column ml-4">
                    <div class="row flex-grow-1">
                        <div class="col border border-dark rounded">
                            <div class="h4 mt-4 font-weight-bold text-center">
                                Code PIN
                            </div>

                            <div class="form-group row mt-4">
                                <label for="badge" class="col-md-6 col-form-label">Badge</label>
                                <div class="col-md-6">
                                    <input type="text" class="form-control" id="badge" v-model="badgeManual">
                                </div>
                            </div>
                            <div class="form-group row">
                                <label for="pin_code" class="col-md-6 col-form-label">Code PIN</label>
                                <div class="col-md-6">
                                    <input type="password" class="form-control" id="pin_code" v-model="pinCode">
                                </div>
                            </div>

                            <div>
                                <button class="btn btn-danger float-right mb-4" :disabled="posting" @click="onSubmitManualPinCode">
                                    <template v-if="posting">
                                        <i class="fad fa-spinner fa-spin"></i> Patientez...
                                    </template>
                                    <template v-else>
                                        Valider
                                    </template>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div v-if="!isInternalEnrolledDevice && userCanLoginWithGoogle" class="row mt-4">
                        <div class="col border border-dark rounded">
                            <div class="h4 mt-4 font-weight-bold text-center">
                                Compte Google
                            </div>
                            <div class="my-4 text-center">
                                <button type="button" class="btn btn-danger btn-social" :disabled="posting" @click="onClickGoogle">
                                    <i class="fab fa-google"></i>
                                    Connectez-vous avec votre<br />compte Google professionnel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
    import Modal from "./Modal";
    import {QrcodeCapture, QrcodeDropZone, QrcodeStream} from "vue-qrcode-reader";
    import ErrorsZone from "./ErrorsZone";

    import {usePostRequest} from "./compositions/axios/postRequest";

    export default {
        components: {
            'modal': Modal,
            'qrcode-stream': QrcodeStream,
            'qrcode-capture': QrcodeCapture,
            'qrcode-dropzone': QrcodeDropZone,
            'errors-zone': ErrorsZone,
        },

        setup() {
            const {
                loading: posting,
                errors: errorsPosting,
                post: doPost
            } = usePostRequest();

            const {
                loading: loadingAuthorizedPeople,
                errors: errorsAuthorizedPeople,
                post: loadAuthorizedPeople,
            } = usePostRequest();

            return {
                posting,
                errorsPosting,
                doPost,

                loadingAuthorizedPeople,
                errorsAuthorizedPeople,
                loadAuthorizedPeople,
            };
        },

        props: {
            tokenType: {
                type: String,
                required: true,
            },
            tokenPayload: {
                type: Object,
                required: true,
            }
        },

        data() {
            return {
                badgeManual: '',
                pinCode: '',
                cameraSelected: 'front',
                cameraDisplayed: false,
                cameraLoading: false,
                cameraNoFront: false,
                cameraNoRear: false,
                cameraError: null,
                cameraValidationPending: false,
                legacyMode: false,
                authorizedPeople: null,
                errorsGooglePopup: [],
            };
        },

        created() {
            let googleApi = document.createElement('script');
            googleApi.setAttribute('src', "https://accounts.google.com/gsi/client");
            document.head.appendChild(googleApi);
        },

        methods: {
            onClickGoogle() {
                this.errorsGooglePopup = [];

                const client = google.accounts.oauth2.initTokenClient({
                    client_id: this.googleClientId,
                    prompt: 'select_account',
                    scope: 'https://www.googleapis.com/auth/userinfo.email',
                    hosted_domain: this.googleParamHd,
                    error_callback: (error) => {
                        if (error.type) {
                            if (error.type === 'popup_failed_to_open') {
                                this.errorsGooglePopup = {message: "Impossible d'ouvrir la popup de connexion Google"};
                                return;
                            }
                            if (error.type === 'popup_closed') {
                                this.errorsGooglePopup = {message: "La popup de connexion Google a été fermée trop rapidement."};
                                return;
                            }
                        }
                        this.errorsGooglePopup = { message: "Une erreur est survenue lors de la connexion avec Google." };
                    },
                    callback: async (response) => {
                        if (response.error) {
                            console.error(response);
                            return;
                        }

                        const authorizationResponse = await this.doPost('/espace-employes/authorisation', {
                            auth_type: 'google',
                            token_type: this.tokenType,
                            payload: JSON.stringify(this.tokenPayload),
                            id_token: response.access_token,
                        });

                        if (authorizationResponse && authorizationResponse.hasOwnProperty('authorization_token') && authorizationResponse.authorization_token) {
                            this.$refs.modal.closeModal();

                            this.$emit('authorised', authorizationResponse.authorization_token);
                        }
                    },
                });
                client.requestAccessToken();
            },

            async onSubmitManualPinCode() {
                const response = await this.doPost('/espace-employes/authorisation', {
                    auth_type: 'manual',
                    token_type: this.tokenType,
                    payload: JSON.stringify(this.tokenPayload),
                    badge: this.badgeManual,
                    pin: this.pinCode,
                });

                this.pinCode = '';

                if (response && response.hasOwnProperty('authorization_token') && response.authorization_token) {
                    this.$refs.modal.closeModal();

                    this.$emit('authorised', response.authorization_token);
                }
            },

            async onOpenModal() {
                this.authorizedPeople = null;

                const response = await this.loadAuthorizedPeople('/espace-employes/personnes-autorisees', {
                    token_type: this.tokenType,
                    payload: JSON.stringify(this.tokenPayload),
                });

                if (response && response.people) {
                    this.authorizedPeople = response.people;
                }
            },

            onCloseModal() {
                this.badgeManual = '';
                this.pinCode = '';
            },

            onClickScan() {
                this.cameraDisplayed = true;
                this.cameraSelected = 'auto';
                this.cameraNoFront = false;
                this.cameraNoRear = false;
                this.cameraError = null;
            },

            async onCameraDecode(decodedString) {
                const cameraOld = this.cameraSelected;
                this.cameraSelected = 'off';
                this.cameraValidationPending = true;

                const response = await this.doPost('/espace-employes/authorisation', {
                    auth_type: 'camera',
                    token_type: this.tokenType,
                    payload: JSON.stringify(this.tokenPayload),
                    badge: decodedString,
                });

                this.cameraValidationPending = false;
                this.cameraSelected = cameraOld;

                if (response && response.hasOwnProperty('authorization_token') && response.authorization_token) {
                    this.$refs.modal.closeModal();

                    this.$emit('authorised', response.authorization_token);
                }
            },

            async onCameraInit(promise) {
                try {
                    this.cameraLoading = true;
                    await promise
                } catch (error) {
                    this.cameraSelected = 'off';
                    this.cameraDisplayed = false;

                    if (error.name === 'NotAllowedError') {
                        this.cameraError = "Erreur: vous devez autoriser l'utilisation de la caméra.";
                    } else if (error.name === 'NotFoundError') {
                        this.cameraError = "Erreur: aucune caméra trouvée sur cet appareil";
                    } else if (error.name === 'NotSupportedError') {
                        this.cameraError = "Erreur: connexion HTTPS requise";
                    } else if (error.name === 'NotReadableError') {
                        this.cameraError = "Erreur: il semblerait que la caméra soit déjà en cours d'utilisation";
                    } else if (error.name === 'OverconstrainedError') {
                        if (this.cameraSelected === 'rear') {
                            this.cameraNoRear = true
                        }
                        if (this.cameraSelected === 'front') {
                            this.cameraNoFront = true
                        }
                        if (!this.cameraNoFront && !this.cameraNoRear) {
                            this.cameraError = "Erreur: caméra(s) introuvable(s) ou incompatible(s)";
                        } else {
                            this.cameraSwitch();
                        }
                    } else if (error.name === 'StreamApiNotSupportedError') {
                        this.legacyMode = true;
                        this.cameraError = "Erreur: votre navigateur web n'est pas compatible avec le module de scan classique, essayez avec le bouton alternatif ci-dessous";
                    }
                } finally {
                    this.cameraLoading = false;
                }
            },

            cameraSwitch() {
                switch (this.camera) {
                    case 'front':
                        this.camera = 'rear'
                        break
                    case 'rear':
                        this.camera = 'front'
                        break
                }
            },
        }
    }
</script>

<style lang="scss">
    .authorization-modal {
        .modal-container {
            max-width: 1100px;
            width: auto;
        }

        .authorization-forms .border {
            border-width: 2px !important;
        }
    }
</style>
